// third-party Chatwoot ActionCable realtime (RoomChannel).
//
// A public/website contact subscribes to Chatwoot's RoomChannel with only its
// `pubsub_token` (returned when we identify the contact). Chatwoot then pushes
// `{ event, data }` frames on that stream — `message.created` when an agent
// replies, etc. We use the socket purely as a REFETCH TRIGGER: on a relevant
// event we re-pull the thread through the typed Client API rather than merging
// raw socket payloads into the query cache. Native + web both expose the global
// WebSocket, so no extra dependency is needed.

const CHATWOOT_BASE_URL =
  process.env.EXPO_PUBLIC_CHATWOOT_BASE_URL ?? "https://app.chatwoot.com";

function cableUrl(): string {
  return (
    CHATWOOT_BASE_URL.replace(/^http/i, "ws").replace(/\/+$/, "") + "/cable"
  );
}

type CableFrame = {
  type?: string;
  identifier?: string;
  message?: { event?: string; data?: unknown };
};

// Events that mean "this contact's thread changed — re-pull it".
const REFETCH_EVENTS = new Set([
  "message.created",
  "message.updated",
  "conversation.status_changed",
]);

/**
 * Subscribe to a contact's Chatwoot RoomChannel and invoke `onChange` whenever a
 * message-affecting event arrives. Returns a cleanup function. Reconnects with
 * capped backoff while active; safe to drive from an effect.
 */
export function subscribeToChatwootRoom(
  pubsubToken: string,
  onChange: () => void,
): () => void {
  const identifier = JSON.stringify({
    channel: "RoomChannel",
    pubsub_token: pubsubToken,
  });

  let socket: WebSocket | null = null;
  let closed = false;
  let reconnectDelay = 1_000;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;

  const scheduleReconnect = () => {
    if (closed || reconnectTimer) return;
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      reconnectDelay = Math.min(reconnectDelay * 2, 15_000);
      connect();
    }, reconnectDelay);
  };

  const connect = () => {
    if (closed) return;
    const ws = new WebSocket(cableUrl());
    socket = ws;

    ws.onopen = () => {
      reconnectDelay = 1_000;
    };
    ws.onmessage = (event) => {
      const raw = event.data;
      if (typeof raw !== "string") return;
      let frame: CableFrame;
      try {
        frame = JSON.parse(raw);
      } catch {
        return;
      }
      // On the ActionCable handshake, confirm the subscription.
      if (frame.type === "welcome") {
        ws.send(JSON.stringify({ command: "subscribe", identifier }));
        return;
      }
      // `ping` / `confirm_subscription` / `reject_subscription` need no action.
      const name = frame.message?.event;
      if (name && REFETCH_EVENTS.has(name)) onChange();
    };
    ws.onerror = () => {
      // Let onclose drive the reconnect; just make sure the socket is torn down.
      try {
        ws.close();
      } catch {
        // ignore
      }
    };
    ws.onclose = () => {
      if (!closed) scheduleReconnect();
    };
  };

  connect();

  return () => {
    closed = true;
    if (reconnectTimer) clearTimeout(reconnectTimer);
    try {
      socket?.close();
    } catch {
      // ignore
    }
  };
}
